import React, { useState, useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Button, Radio, Spin, Empty, Switch, Alert } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
// FLAT BUFFERS
import { ElevatorControl } from "../../../flatbuffers/generated/control/elevator-control";
// INTERFACES
import { BankFloorInterface } from "../../../interfaces/Bank.interface";
import { LiftFloorInterface } from "../../../interfaces/Lift.interface";
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { DeviceServiceContent } from "../../../flatbuffers/generated/service/device-service-content";
import { ElevatorService } from "../../../flatbuffers/generated/service/elevator-service";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// UUID
import { stringToUuidT } from "../../../utils/uuid";

export default function ControlPanelLiftElevator({
  hasFrontDoors,
  hasRearDoors,
}: {
  hasFrontDoors: boolean;
  hasRearDoors: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [doorSelected, setDoorSelected] = useState<"FRONT" | "REAR">("FRONT");
  const [spinningFloorNo, setSpinningFloorNo] = useState<number>(0);
  const [secureCalls, setSecureCalls] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(true);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { permissions } = useSelector((state: RootState) => state.user);
  const { liftSelected, bankSelected } = useSelector(
    (state: RootState) => state.monitoring
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { triggerPostCommand, isLoadingPostCommand } = usePostCommand();

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(
    () => stringToUuidT(liftSelected.id),
    [liftSelected.id]
  );
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasBankFloorIndexZero = useMemo(
    () =>
      (bankSelected.bank_floors || []).some(
        (floor: BankFloorInterface) => floor.floor_index === 0
      ),
    [bankSelected.bank_floors]
  );
  const liftFloorsByBankFloorId = useMemo(() => {
    const liftFloorsMap = new Map<string, LiftFloorInterface>();
    (liftSelected.lift_floors || []).forEach((liftFloor: LiftFloorInterface) => {
      liftFloorsMap.set(liftFloor.bank_floor_id, liftFloor);
    });
    return liftFloorsMap;
  }, [liftSelected.lift_floors]);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickCarCall = async (floor: number) => {
    const shouldSubtractOne =
      bankSelected.start_floor_no === 1 && !hasBankFloorIndexZero;
    const floorToSend = Math.max(0, shouldSubtractOne ? floor - 1 : floor);
    let elevatorControl = null;
    if (secureCalls) {
      if (doorSelected === "FRONT") {
        elevatorControl = ElevatorControl.CarCallLock;
      } else {
        elevatorControl = ElevatorControl.RearCarCallLock;
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (doorSelected === "FRONT") {
        elevatorControl = ElevatorControl.EnergizeCarCall;
      } else {
        elevatorControl = ElevatorControl.EnergizeRearCarCall;
      }
    }
    // POST COMMAND
    triggerPostCommand({
      floor: floorToSend,
      command: elevatorControl,
      liftType: "ELEVATOR",
      active: true,
    });
  };

  const onChangeSecureCalls = (checked: boolean) => {
    setSecureCalls(checked);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    setShowLoading(true);
    setTimeout(() => {
      setShowLoading(false);
    }, 500);
  }, []);

  useEffect(() => {
    if (!hasFrontDoors && hasRearDoors) {
      setDoorSelected("REAR");
    } else if (hasFrontDoors && !hasRearDoors) {
      setDoorSelected("FRONT");
    } else {
      setDoorSelected("FRONT");
    }
  }, [hasFrontDoors, hasRearDoors]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="monitoringView__detailsBox">
      <div
        className={`monitoringView__controlPanel monitoringView__controlPanel__${theme}`}
        style={{
          minHeight: "400px",
        }}
      >
        <Spin
          spinning={showLoading || isLoadingPostCommand || isLoadingPostCommand}
          style={{ paddingTop: "250px" }}
        >
          {!showLoading && (
            <>
              {/** ACTIONS */}
              {(hasFrontDoors || hasRearDoors) && (
                <div className={` drawer__box__control__panel__${theme} mt-3`}>
                  <div className="generalStyles__spaceBetween">
                    <div className="generalStyles__flex">
                      <Radio.Group
                        value={doorSelected}
                        onChange={(e) => {
                          setDoorSelected(e.target.value);
                        }}
                      >
                        {hasFrontDoors && (
                          <Radio value="FRONT">{t("general.frontDoors")}</Radio>
                        )}
                        {hasRearDoors && (
                          <Radio value="REAR">{t("general.rearDoors")}</Radio>
                        )}
                      </Radio.Group>
                    </div>
                    {validatePermission(
                      "monitor-control-panel-secure-car-calls",
                      permissions
                    ) && (
                        <div>
                          {secureCalls && (
                            <FontAwesomeIcon
                              icon={faLock}
                              fontSize={18}
                              className="monitoring__modalSettings__icon generalStyles__mrFix"
                            />
                          )}
                          <span
                            className="generalStyles__mrFix"
                            style={{ marginTop: "5px" }}
                          >
                            {t("general.secureCalls")}
                          </span>
                          <Switch
                            checked={secureCalls}
                            onChange={onChangeSecureCalls}
                          />
                        </div>
                      )}
                  </div>
                </div>
              )}
              {/** PANEL / CIRCLE BUTTONS */}
              <div className={`drawer__box__control__panel__${theme} mt-3`}>
                <div className="monitoringView__controlPanelPanel">
                  {socketMessageLift.serviceMode?.messageType ===
                    DeviceServiceContent.ElevatorServiceMessage &&
                    socketMessageLift.serviceMode?.message?.message ===
                    ElevatorService.VIP ? (
                    <>
                      <Alert
                        className="generalStyles__width100"
                        message="VIP"
                        description={t("general.vipModeActive")}
                        type="warning"
                        showIcon
                      />
                    </>
                  ) : (
                    <>
                      {hasFrontDoors || hasRearDoors ? (
                        <>
                          {bankSelected.bank_floors && bankSelected.bank_floors
                            .slice()
                            .sort(
                              (a: BankFloorInterface, b: BankFloorInterface) =>
                                b.floor_index - a.floor_index
                            )
                            .map((floor: BankFloorInterface) => {
                              const matchedLiftFloor = liftFloorsByBankFloorId.get(
                                floor.id
                              );
                              const frontDoorEnabled =
                                matchedLiftFloor?.front_door ?? floor.front_door;
                              const rearDoorEnabled =
                                matchedLiftFloor?.rear_door ?? floor.rear_door;

                              return (
                                <div key={floor.id}>
                                  <Button
                                    className={`monitoring__modalSettings__button__1 ${liftSelected &&
                                      socketMessageLift.position ===
                                      floor.floor_index
                                      ? "monitoringView__controlPanelNumber__active"
                                      : `monitoringView__controlPanelNumber__${theme}`
                                      }`}
                                    shape="circle"
                                    onClick={() => {
                                      onClickCarCall(floor.floor_index);
                                      setSpinningFloorNo(floor.floor_index);
                                    }}
                                    disabled={
                                      (doorSelected === "FRONT" &&
                                        !frontDoorEnabled) ||
                                      (doorSelected === "REAR" &&
                                        !rearDoorEnabled) ||
                                      (socketMessageLift.carCalls.frontLock.includes(
                                        floor.floor_index
                                      ) &&
                                        doorSelected === "FRONT" &&
                                        !secureCalls) ||
                                      (socketMessageLift.carCalls.rearLock.includes(
                                        floor.floor_index
                                      ) &&
                                        doorSelected === "REAR" &&
                                        !secureCalls)
                                    }
                                  >
                                  {(socketMessageLift.carCalls.frontLock.includes(
                                    floor.floor_index
                                  ) &&
                                    doorSelected === "FRONT") ||
                                    (socketMessageLift.carCalls.rearLock.includes(
                                      floor.floor_index
                                    ) &&
                                      doorSelected === "REAR") ? (
                                    <div>
                                      <div style={{ marginTop: "5px" }}>
                                        <span>
                                          {floor &&
                                            floor.name &&
                                            floor.name !== ""
                                            ? floor.name
                                              .substring(0, 3)
                                              .toUpperCase()
                                            : floor.floor_index}
                                        </span>
                                      </div>
                                      <div>
                                        <FontAwesomeIcon
                                          icon={faLock}
                                          fontSize={11}
                                          className="monitoring__modalSettings__icon"
                                          style={{ marginTop: "16px" }}
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      {isLoadingPostCommand &&
                                        spinningFloorNo === floor.floor_index ? (
                                        <Spin
                                          spinning={
                                            isLoadingPostCommand &&
                                            spinningFloorNo ===
                                            floor.floor_index
                                          }
                                        />
                                      ) : (
                                        <span style={{ marginTop: "2px" }}>
                                          {floor &&
                                            floor.name &&
                                            floor.name !== ""
                                            ? floor.name
                                              .substring(0, 3)
                                              .toUpperCase()
                                            : floor.floor_index}
                                        </span>
                                      )}
                                    </>
                                  )}
                                  </Button>
                                </div>
                              );
                            })}
                        </>
                      ) : (
                        <Empty
                          description={
                            <span>{t("general.bankHasNoFrontRearDoors")}</span>
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </Spin>
      </div>
    </div>
  );
}
